import React, { useState, useEffect, useRef } from 'react';
import { FiCheckSquare, FiSquare, FiMinus, FiMessageCircle, FiCheck, FiX, FiAlertTriangle, FiPlus, FiTrash2 } from 'react-icons/fi';
import axios from 'axios';
import { API_URL } from '../config';
import { getAuthToken } from '../services/api';

const getAuthConfig = async () => {
  const token = await getAuthToken();
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

/**
 * FieldChecklistModal — three modes:
 *   mode="overview"  — read-only preview on timer start (dismiss only)
 *   mode="edit"      — mid-job editing (save + close)
 *   mode="clockout"  — stop timer flow (save & clock out)
 *
 * Props:
 *   jobId, jobNumber        — required
 *   checklistId             — if set, loads existing checklist; otherwise creates new
 *   mode                    — "overview" | "edit" | "clockout"
 *   onComplete              — called after save on clockout, or dismiss on overview
 *   onClose                 — close without action
 */
function FieldChecklistModal({ jobId, jobNumber, checklistId, mode = 'edit', onComplete, onClose }) {
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [itemStates, setItemStates] = useState({});
  const [expandedNote, setExpandedNote] = useState(null);
  const [newItemText, setNewItemText] = useState('');
  const [addingItem, setAddingItem] = useState(false);
  const [commentDrafts, setCommentDrafts] = useState({}); // {itemId: draft text}
  const [postingComment, setPostingComment] = useState(null); // itemId currently posting
  // Each tap saves itself (asked 2026-10-24): checks used to live only on screen until
  // Save, so a closed app or dead phone lost them. Taps within AUTOSAVE_MS go out as
  // one request, and requests run one at a time so a quick checked → N/A can't land
  // out of order. Offline, the save queues in the outbox like any other.
  const [autoSave, setAutoSave] = useState(''); // '' | 'saving' | 'saved' | 'queued' | 'error'
  const dirty = useRef({});           // {itemId: status} not yet sent
  const flushTimer = useRef(null);
  const saveChain = useRef(Promise.resolve());
  const checklistRef = useRef(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const isOverview = mode === 'overview';
  const isClockout = mode === 'clockout';

  const formatTimeAgo = (iso) => {
    if (!iso) return '';
    let s = iso;
    if (!s.endsWith('Z') && !s.includes('+')) s = s + 'Z';
    const d = new Date(s);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  };

  const formatFullTime = (iso) => {
    if (!iso) return '';
    let s = iso;
    if (!s.endsWith('Z') && !s.includes('+')) s = s + 'Z';
    const d = new Date(s);
    return d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const handleAddComment = async (itemId) => {
    const text = (commentDrafts[itemId] || '').trim();
    if (!text || !checklist) return;
    setPostingComment(itemId);
    try {
      const config = await getAuthConfig();
      const res = await axios.post(
        `${API_URL}/field-checklists/${checklist.id}/items/${itemId}/comments`,
        { text },
        config
      );
      if (res.data && res.data.__queued) {
        // Offline: the comment is in the outbox. Show it locally rather than
        // replacing the checklist with the queue placeholder.
        const pending = { id: `pending-${res.data.client_op_id}`, text, author_name: 'You (pending sync)', created_at: new Date().toISOString() };
        setChecklist(prev => ({
          ...prev,
          categories: Object.fromEntries(Object.entries(prev.categories || {}).map(([cat, items]) => [
            cat, items.map(i => i.id === itemId ? { ...i, comments: [...(i.comments || []), pending] } : i),
          ])),
        }));
      } else {
        setChecklist(res.data);
        mergeStates(res.data);
      }
      setCommentDrafts(prev => ({ ...prev, [itemId]: '' }));
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to post comment');
    }
    setPostingComment(null);
  };

  const handleDeleteComment = async (itemId, commentId) => {
    if (!checklist) return;
    if (!window.confirm('Delete this comment?')) return;
    try {
      const config = await getAuthConfig();
      const res = await axios.delete(
        `${API_URL}/field-checklists/${checklist.id}/items/${itemId}/comments/${commentId}`,
        config
      );
      setChecklist(res.data);
      mergeStates(res.data);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to delete comment');
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const config = await getAuthConfig();
        if (checklistId) {
          // Load existing checklist
          const res = await axios.get(`${API_URL}/field-checklists/${checklistId}`, config);
          setChecklist(res.data);
          seedStates(res.data);
        } else {
          // Check for active checklist on this job
          const activeRes = await axios.get(`${API_URL}/jobs/${jobId}/field-checklists/active`, config);
          if (activeRes.data.active) {
            setChecklist(activeRes.data.active);
            seedStates(activeRes.data.active);
          } else {
            // Create new
            const createRes = await axios.post(`${API_URL}/field-checklists/create`, {
              job_id: jobId, checklist_type: 'boundary',
            }, config);
            setChecklist(createRes.data);
            seedStates(createRes.data);
          }
        }
      } catch (error) {
        console.error('Field checklist init error:', error);
        // If we can't load or create the checklist, don't silently disappear —
        // fall through so the parent's post-clockout flow can still continue.
        if (onClose) onClose();
        return;
      }
      setLoading(false);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, checklistId]);

  // Comments and added/removed items reload the checklist from the server, but
  // check-offs live only here until Save. Keep the local statuses for items we
  // already have, or posting a comment wipes every unsaved check.
  const mergeStates = (data) => {
    setItemStates(prev => {
      const states = {};
      Object.values(data.categories || {}).forEach(items => {
        items.forEach(item => {
          states[item.id] = prev[item.id] || { status: item.status || 'unchecked' };
        });
      });
      return states;
    });
  };

  const seedStates = (data) => {
    const states = {};
    Object.values(data.categories || {}).forEach(items => {
      items.forEach(item => {
        states[item.id] = { status: item.status || 'unchecked' };
      });
    });
    setItemStates(states);
  };

  const allDone = Object.values(itemStates).length > 0 &&
    Object.values(itemStates).every(s => s.status === 'checked' || s.status === 'na');

  const totals = {
    total: Object.keys(itemStates).length,
    checked: Object.values(itemStates).filter(s => s.status === 'checked').length,
    na: Object.values(itemStates).filter(s => s.status === 'na').length,
    unchecked: Object.values(itemStates).filter(s => s.status === 'unchecked').length,
  };

  const progressPct = totals.total > 0
    ? Math.round(((totals.checked + totals.na) / totals.total) * 100) : 0;

  // Check if Equipment & Data items are all done (soft reminder on clock-out)
  const equipmentItems = checklist ? Object.entries(checklist.categories || {})
    .filter(([cat]) => cat === 'Equipment & Data' || cat === 'Communication')
    .flatMap(([, items]) => items) : [];
  const equipmentUnchecked = equipmentItems.filter(i => {
    const s = itemStates[i.id];
    return s && s.status === 'unchecked';
  });

  checklistRef.current = checklist;

  const flushAutoSave = () => {
    clearTimeout(flushTimer.current);
    flushTimer.current = null;
    const cl = checklistRef.current;
    const items = Object.entries(dirty.current).map(([id, status]) => ({ id: parseInt(id), status }));
    if (!cl || items.length === 0) return saveChain.current;
    dirty.current = {};
    setAutoSave('saving');
    saveChain.current = saveChain.current.then(async () => {
      try {
        const config = await getAuthConfig();
        const res = await axios.put(`${API_URL}/field-checklists/${cl.id}/save`, { items }, config);
        setAutoSave(res.data && res.data.__queued ? 'queued' : 'saved');
      } catch (e) {
        // Put them back so the next tap, Save, or close retries them.
        items.forEach(({ id, status }) => { if (!(id in dirty.current)) dirty.current[id] = status; });
        setAutoSave('error');
      }
    });
    return saveChain.current;
  };

  // Send anything still pending if the modal goes away without a button press.
  useEffect(() => () => { flushAutoSave(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cycleStatus = (itemId) => {
    if (isOverview) return;
    const current = itemStates[itemId]?.status || 'unchecked';
    const next = current === 'unchecked' ? 'checked' : current === 'checked' ? 'na' : 'unchecked';
    setItemStates(prev => ({ ...prev, [itemId]: { ...prev[itemId], status: next } }));
    dirty.current[itemId] = next;
    clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(flushAutoSave, 600);
  };

  const closeModal = async () => {
    await flushAutoSave();
    if (onClose) onClose();
  };

  const handleSave = async () => {
    if (!checklist) return;
    setSaving(true);
    clearTimeout(flushTimer.current);
    dirty.current = {};
    await saveChain.current;
    try {
      const config = await getAuthConfig();
      const itemsPayload = Object.entries(itemStates).map(([id, state]) => ({
        id: parseInt(id), status: state.status,
      }));
      const res = await axios.put(`${API_URL}/field-checklists/${checklist.id}/save`, { items: itemsPayload }, config);
      // Offline the save is queued; keep what's on screen instead of the placeholder.
      const saved = res.data && res.data.__queued ? checklist : res.data;
      setChecklist(saved);
      if (!(res.data && res.data.__queued)) seedStates(res.data);
      if (onComplete) onComplete(saved);
    } catch (error) {
      alert('Failed to save checklist');
    }
    setSaving(false);
  };

  const handleSaveAndClose = async () => {
    await handleSave();
    if (onClose) onClose();
  };

  const handleAddItem = async () => {
    if (!newItemText.trim() || !checklist) return;
    setAddingItem(true);
    try {
      const config = await getAuthConfig();
      const res = await axios.post(`${API_URL}/field-checklists/${checklist.id}/items`, {
        item_text: newItemText.trim(),
      }, config);
      setChecklist(res.data);
      mergeStates(res.data);
      setNewItemText('');
    } catch (error) {
      alert('Failed to add item');
    }
    setAddingItem(false);
  };

  const handleDeleteItem = async (itemId) => {
    if (!checklist) return;
    try {
      const config = await getAuthConfig();
      await axios.delete(`${API_URL}/field-checklists/${checklist.id}/items/${itemId}`, config);
      // Reload checklist
      const res = await axios.get(`${API_URL}/field-checklists/${checklist.id}`, config);
      setChecklist(res.data);
      mergeStates(res.data);
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete item');
    }
  };

  const renderComments = (item) => {
    if (expandedNote !== item.id || isOverview) return null;
    const draft = commentDrafts[item.id] || '';
    const comments = item.comments || [];
    return (
      <div style={{ padding: '0 20px 12px 56px', backgroundColor: '#fafafa' }}>
        {comments.length === 0 && (
          <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '6px', fontStyle: 'italic' }}>
            No comments yet — add one below.
          </div>
        )}
        {comments.map(c => (
          <div key={c.id} style={{
            padding: '8px 10px', backgroundColor: 'white', borderRadius: '6px',
            marginBottom: '6px', border: '1px solid #f3f4f6',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'center' }}>
              <strong style={{ fontSize: '12px', color: '#111827' }}>{c.author_name || 'Crew'}</strong>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '11px', color: '#9ca3af' }} title={formatFullTime(c.created_at)}>
                  {formatTimeAgo(c.created_at)}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteComment(item.id, c.id); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '2px' }}
                  title="Delete comment"
                >
                  <FiTrash2 size={11} />
                </button>
              </div>
            </div>
            <div style={{ fontSize: '13px', color: '#374151', marginTop: '2px', whiteSpace: 'pre-wrap' }}>
              {c.text}
            </div>
          </div>
        ))}
        <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
          <input
            type="text"
            value={draft}
            onChange={(e) => setCommentDrafts(prev => ({ ...prev, [item.id]: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter' && draft.trim()) handleAddComment(item.id); }}
            placeholder="Add a comment..."
            autoFocus
            style={{
              flex: 1, padding: '8px 10px', border: '1px solid #d1d5db',
              borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box',
            }}
          />
          <button
            onClick={() => handleAddComment(item.id)}
            disabled={!draft.trim() || postingComment === item.id}
            style={{
              padding: '8px 14px',
              backgroundColor: draft.trim() ? '#2563eb' : '#9ca3af',
              color: 'white', border: 'none', borderRadius: '6px',
              cursor: draft.trim() ? 'pointer' : 'not-allowed',
              fontSize: '13px', fontWeight: '600', flexShrink: 0,
            }}
          >
            {postingComment === item.id ? '...' : 'Post'}
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 999999,
      }}>
        <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '30px', textAlign: 'center', color: '#6b7280' }}>
          Loading checklist...
        </div>
      </div>
    );
  }

  if (!checklist) return null;

  const StatusIcon = ({ status }) => {
    if (status === 'checked') return <FiCheckSquare size={22} color="#16a34a" />;
    if (status === 'na') return <FiMinus size={22} color="#6b7280" />;
    return <FiSquare size={22} color="#d1d5db" />;
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 999999,
      padding: isMobile ? '8px' : '16px',
    }}>
      <div style={{
        backgroundColor: 'white', borderRadius: '16px', overflow: 'hidden',
        maxWidth: '600px', width: '100%', maxHeight: 'calc(90dvh - 90px)', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h3 style={{ margin: '0 0 4px', fontSize: '17px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FiCheckSquare color="#2563eb" />
                {isOverview ? 'Job Checklist Overview' : 'Field Departure Checklist'}
              </h3>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>
                {jobNumber || `Job #${jobId}`}
                {checklist.name && checklist.name !== 'Field Checklist' && (
                  <span style={{ marginLeft: '8px', color: '#2563eb', fontWeight: '500' }}>— {checklist.name}</span>
                )}
              </div>
            </div>
            <button onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px' }}>
              <FiX size={20} />
            </button>
          </div>

          {/* Progress bar */}
          <div style={{ marginTop: '10px' }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', fontSize: '12px',
              color: '#374151', fontWeight: '500', marginBottom: '4px',
            }}>
              <span>{totals.checked + totals.na} of {totals.total}</span>
              <span>{progressPct}%</span>
            </div>
            <div style={{ height: '8px', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: '4px', transition: 'width 0.2s',
                width: `${progressPct}%`,
                backgroundColor: allDone ? '#16a34a' : '#2563eb',
              }} />
            </div>
          </div>

          {isOverview && (
            <div style={{ fontSize: '12px', color: '#2563eb', marginTop: '8px', fontWeight: '500' }}>
              Review the items below — check them off as you complete them during the job.
            </div>
          )}
          {!isOverview && (
            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px' }}>
              Tap item: unchecked → checked ✓ → N/A. Each tap saves on its own. Tap the comment icon to leave a timestamped note.
              {autoSave && (
                <span style={{
                  marginLeft: '6px', fontWeight: 600,
                  color: autoSave === 'error' ? '#dc2626' : autoSave === 'queued' ? '#d97706' : '#16a34a',
                }}>
                  {autoSave === 'saving' ? 'Saving…'
                    : autoSave === 'saved' ? '✓ Saved'
                    : autoSave === 'queued' ? 'Offline — will sync'
                    : "Couldn't save — tap Save to retry"}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Scrollable items */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {/* IMPORTANT ITEMS — custom items shown first with highlighting */}
          {(() => {
            const customItems = [];
            Object.values(checklist.categories || {}).forEach(items => {
              items.forEach(item => { if (item.is_custom) customItems.push(item); });
            });
            if (customItems.length === 0) return null;
            return (
              <div key="__important">
                <div style={{
                  padding: '8px 20px', backgroundColor: '#dc2626', color: 'white',
                  fontSize: '12px', fontWeight: '700', letterSpacing: '0.5px',
                  position: 'sticky', top: 0, zIndex: 2,
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}>
                  ⚠ IMPORTANT ITEMS
                </div>
                {customItems.map(item => {
                  const state = itemStates[item.id] || { status: item.status || 'unchecked', note: '' };
                  const isChecked = state.status === 'checked';
                  const isNA = state.status === 'na';
                  return (
                    <div key={item.id} style={{ borderBottom: '1px solid #fca5a5' }}>
                      <div
                        onClick={() => cycleStatus(item.id)}
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: '12px',
                          padding: '12px 20px', cursor: isOverview ? 'default' : 'pointer',
                          backgroundColor: isChecked ? '#f0fdf4' : isNA ? '#f9fafb' : '#fef2f2',
                          borderLeft: '4px solid #dc2626',
                          minHeight: '44px',
                        }}
                      >
                        <div style={{ flexShrink: 0, marginTop: '1px', textAlign: 'center' }}>
                          <StatusIcon status={state.status} />
                          {isChecked && <div style={{ color: '#16a34a', fontSize: '9px', fontWeight: '600' }}>DONE</div>}
                          {isNA && <div style={{ color: '#6b7280', fontSize: '9px', fontWeight: '600' }}>N/A</div>}
                        </div>
                        <div style={{
                          flex: 1, fontSize: '14px', lineHeight: '1.4', fontWeight: '600',
                          color: isChecked ? '#166534' : isNA ? '#9ca3af' : '#991b1b',
                          textDecoration: isNA ? 'line-through' : 'none',
                        }}>
                          {item.item_text}
                        </div>
                        {!isOverview && (
                          <>
                          <button
                            onClick={(e) => { e.stopPropagation(); setExpandedNote(expandedNote === item.id ? null : item.id); }}
                            style={{ flexShrink: 0, padding: '4px', background: 'none', border: 'none', cursor: 'pointer', color: (item.comments && item.comments.length) ? '#2563eb' : '#d1d5db', position: 'relative' }}
                            title="Comments"
                          >
                            <FiMessageCircle size={16} />
                            {item.comments && item.comments.length > 0 && (
                              <span style={{ marginLeft: '2px', fontSize: '10px', fontWeight: 700 }}>{item.comments.length}</span>
                            )}
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteItem(item.id); }}
                            style={{ flexShrink: 0, padding: '4px', background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}
                            title="Remove item"
                          >
                            <FiTrash2 size={14} />
                          </button>
                          </>
                        )}
                      </div>
                      {renderComments(item)}
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Standard categories */}
          {/* The server sends categories alphabetized; job work orders go first. */}
          {Object.entries(checklist.categories || {})
            .sort(([a], [b]) => (b === 'Work Orders') - (a === 'Work Orders'))
            .map(([category, items]) => {
            // Filter out custom items since they're shown above
            const standardItems = items.filter(i => !i.is_custom);
            if (standardItems.length === 0) return null;
            // Work orders get the same red treatment as IMPORTANT ITEMS so they stand out.
            const isWO = category === 'Work Orders';
            return (
            <div key={category}>
              <div style={{
                padding: '8px 20px', backgroundColor: isWO ? '#dc2626' : '#1e40af', color: 'white',
                fontSize: '12px', fontWeight: isWO ? '700' : '600', letterSpacing: isWO ? '0.5px' : '0.3px',
                position: 'sticky', top: 0, zIndex: 1,
              }}>
                {isWO ? '⚠ WORK ORDERS' : category}
              </div>
              {standardItems.map(item => {
                const state = itemStates[item.id] || { status: item.status || 'unchecked', note: '' };
                const isChecked = state.status === 'checked';
                const isNA = state.status === 'na';
                return (
                  <div key={item.id} style={{ borderBottom: isWO ? '1px solid #fca5a5' : '1px solid #f3f4f6' }}>
                    <div
                      onClick={() => cycleStatus(item.id)}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: '12px',
                        padding: '12px 20px', cursor: isOverview ? 'default' : 'pointer',
                        backgroundColor: isChecked ? '#f0fdf4' : isNA ? '#f9fafb' : isWO ? '#fef2f2' : 'white',
                        borderLeft: isWO ? '4px solid #dc2626' : undefined,
                        minHeight: '44px',
                      }}
                    >
                      <div style={{ flexShrink: 0, marginTop: '1px', textAlign: 'center' }}>
                        <StatusIcon status={state.status} />
                        {isChecked && <div style={{ color: '#16a34a', fontSize: '9px', fontWeight: '600' }}>DONE</div>}
                        {isNA && <div style={{ color: '#6b7280', fontSize: '9px', fontWeight: '600' }}>N/A</div>}
                      </div>
                      <div style={{
                        flex: 1, fontSize: '14px', lineHeight: '1.4', fontWeight: isWO ? '600' : undefined,
                        color: isChecked ? '#166534' : isNA ? '#9ca3af' : isWO ? '#991b1b' : '#374151',
                        textDecoration: isNA ? 'line-through' : 'none',
                      }}>
                        {item.item_text}
                        {item.is_custom && (
                          <span style={{
                            display: 'inline-block', marginLeft: '6px',
                            padding: '1px 6px', fontSize: '10px', fontWeight: '600',
                            backgroundColor: '#eff6ff', color: '#2563eb', borderRadius: '4px',
                          }}>CUSTOM</span>
                        )}
                      </div>
                      {!isOverview && (
                        <>
                        <button
                          onClick={(e) => { e.stopPropagation(); setExpandedNote(expandedNote === item.id ? null : item.id); }}
                          style={{
                            flexShrink: 0, padding: '4px', background: 'none', border: 'none',
                            cursor: 'pointer', color: (item.comments && item.comments.length) ? '#2563eb' : '#d1d5db',
                            display: 'flex', alignItems: 'center', gap: '2px',
                          }}
                          title="Comments"
                        >
                          <FiMessageCircle size={16} />
                          {item.comments && item.comments.length > 0 && (
                            <span style={{ fontSize: '10px', fontWeight: 700 }}>{item.comments.length}</span>
                          )}
                        </button>
                        {item.is_custom && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteItem(item.id); }}
                            style={{
                              flexShrink: 0, padding: '4px', background: 'none', border: 'none',
                              cursor: 'pointer', color: '#dc2626',
                            }}
                            title="Remove custom item"
                          >
                            <FiTrash2 size={14} />
                          </button>
                        )}
                        </>
                      )}
                    </div>
                    {renderComments(item)}
                  </div>
                );
              })}
            </div>
            );
          })}

          {/* Add custom item inline */}
          {!isOverview && (
            <div style={{
              padding: '12px 20px', borderTop: '1px solid #e5e7eb', backgroundColor: '#f9fafb',
            }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                <FiPlus size={12} /> Add a custom item to this checklist
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input
                  type="text"
                  value={newItemText}
                  onChange={(e) => setNewItemText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddItem()}
                  placeholder="e.g., Check septic tank location..."
                  style={{
                    flex: 1, padding: '8px 10px', border: '1px solid #d1d5db',
                    borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box',
                  }}
                />
                <button
                  onClick={handleAddItem}
                  disabled={!newItemText.trim() || addingItem}
                  style={{
                    padding: '8px 14px',
                    backgroundColor: newItemText.trim() ? '#2563eb' : '#9ca3af',
                    color: 'white', border: 'none', borderRadius: '6px',
                    cursor: newItemText.trim() ? 'pointer' : 'not-allowed',
                    fontSize: '13px', fontWeight: '600', flexShrink: 0,
                    display: 'flex', alignItems: 'center', gap: '4px',
                  }}
                >
                  <FiPlus size={14} /> Add
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px', borderTop: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: '8px', backgroundColor: '#f9fafb', flexWrap: 'wrap',
        }}>
          {isOverview ? (
            <>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                {totals.unchecked} items to complete
              </div>
              <button
                onClick={() => { if (onComplete) onComplete(checklist); }}
                style={{
                  padding: '12px 24px', border: 'none', borderRadius: '8px', cursor: 'pointer',
                  fontSize: '14px', fontWeight: '700', backgroundColor: '#2563eb', color: 'white',
                }}
              >
                Got It — Start Working
              </button>
            </>
          ) : isClockout ? (
            <>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                {equipmentUnchecked.length > 0 && (
                  <span style={{ color: '#d97706', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <FiAlertTriangle size={14} /> {equipmentUnchecked.length} equipment/data item{equipmentUnchecked.length !== 1 ? 's' : ''} unchecked
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={closeModal} style={{
                  padding: '10px 16px', backgroundColor: 'white', border: '1px solid #d1d5db',
                  borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: '#6b7280',
                }}>
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    padding: '12px 24px', border: 'none', borderRadius: '8px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontSize: '14px', fontWeight: '700',
                    backgroundColor: '#16a34a', color: 'white',
                    display: 'flex', alignItems: 'center', gap: '8px',
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  <FiCheck size={16} /> {saving ? 'Saving...' : 'Save & Clock Out'}
                </button>
              </div>
            </>
          ) : (
            /* edit mode */
            <>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                {totals.unchecked > 0 ? `${totals.unchecked} items remaining` : 'All items complete!'}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={closeModal} style={{
                  padding: '10px 16px', backgroundColor: 'white', border: '1px solid #d1d5db',
                  borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: '#6b7280',
                }}>
                  Close
                </button>
                <button
                  onClick={handleSaveAndClose}
                  disabled={saving}
                  style={{
                    padding: '10px 20px', border: 'none', borderRadius: '8px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontSize: '13px', fontWeight: '600',
                    backgroundColor: '#2563eb', color: 'white',
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  {saving ? 'Saving...' : 'Save Progress'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default FieldChecklistModal;
